import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { GATEWAY_OWNERSHIPS, GATEWAY_STATUSES, type Gateway } from "@/contracts";

import { gatewaysQuery, type GatewayFilters } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDevEui, formatRelative } from "@/lib/format";
import { useListFilters } from "@/lib/use-list-filters";
import { GatewayAlertDialog } from "./gateway-alert-dialog";
import { GatewayCreateDialog } from "./gateway-create-dialog";
import { GatewayStatusBadge } from "./gateway-badges";
import { GatewayOutagesCard } from "./gateway-outages-card";
import { GATEWAY_OWNERSHIP_LABELS, GATEWAY_STATUS_LABELS } from "./gateway-labels";

/**
 * The gateways a tenant can see are their own, and that is the whole list.
 *
 * Shared platform infrastructure is missing on purpose rather than by
 * oversight: a customer whose sensors report through one of our gateways is
 * told their coverage is provided, not which box provides it (ADR-062). Only
 * a super admin has a reason — and the right — to see the inventory.
 */
export function GatewaysPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const canCreate = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [creating, setCreating] = useState(false);
  const [editingAlert, setEditingAlert] = useState<Gateway | null>(null);

  const filters: GatewayFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    ...(filterValue("status") ? { status: filterValue("status") } : {}),
    ...(filterValue("ownership") ? { ownership: filterValue("ownership") } : {}),
    ...(filterValue("siteId") ? { siteId: filterValue("siteId") } : {}),
    ...(filterValue("shared") === "true" ? { shared: true } : {}),
    page,
  };

  const gateways = useQuery(gatewaysQuery(client, filters));

  const columnCount = isSuperAdmin ? 8 : 6;

  return (
    <>
      <PageHeader
        title={t("gatewaysTitle")}
        description={t("gatewaysDescription")}
        action={
          canCreate ? <Button onClick={() => setCreating(true)}>{t("addGateway")}</Button> : null
        }
      />

      {isSuperAdmin ? <GatewayOutagesCard /> : null}

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filterValue("search")}
              placeholder={t("searchGateways")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={tCommon("status")}
              value={filterValue("status")}
              onChange={(value) => setFilter("status", value)}
              data={[
                { value: "", label: tCommon("anyStatus") },
                ...GATEWAY_STATUSES.map((status) => ({
                  value: status,
                  label: GATEWAY_STATUS_LABELS[status],
                })),
              ]}
            />

            <SelectField
              label={tCommon("ownedBy")}
              value={filterValue("ownership")}
              onChange={(value) => setFilter("ownership", value)}
              data={[
                { value: "", label: tCommon("anyone") },
                ...GATEWAY_OWNERSHIPS.map((ownership) => ({
                  value: ownership,
                  label: GATEWAY_OWNERSHIP_LABELS[ownership],
                })),
              ]}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}

            {isSuperAdmin ? (
              <SelectField
                label={tCommon("ownershipScope")}
                value={filterValue("shared")}
                onChange={(value) => setFilter("shared", value)}
                data={[
                  { value: "", label: tCommon("everyGateway") },
                  { value: "true", label: tCommon("sharedInfrastructureOnly") },
                ]}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {gateways.isPending ? <LoadingBlock label={t("loadingGateways")} /> : null}

      {gateways.isError ? (
        <ErrorBlock error={gateways.error} onRetry={() => void gateways.refetch()} />
      ) : null}

      {gateways.data ? (
        <Card>
          <DataTable caption="Gateways">
            <thead>
              <tr>
                {isSuperAdmin ? <Th>{tCommon("organization")}</Th> : null}
                <Th>{tCommon("name")}</Th>
                <Th>{t("colEui")}</Th>
                <Th>{tCommon("site")}</Th>
                <Th>{tCommon("status")}</Th>
                <Th numeric>{t("colDevices")}</Th>
                <Th>{t("colLastHeard")}</Th>
                {isSuperAdmin ? <Th>{t("colOutageAlert")}</Th> : null}
              </tr>
            </thead>

            <tbody>
              {gateways.data.data.length === 0 ? (
                <EmptyRow colSpan={columnCount}>{t("emptyGateways")}</EmptyRow>
              ) : null}

              {gateways.data.data.map((gateway) => (
                <tr key={gateway.id}>
                  {isSuperAdmin ? (
                    <Td>{gateway.organizationName ?? tCommon("sharedInfrastructure")}</Td>
                  ) : null}
                  <Td className="font-medium">{gateway.name}</Td>
                  <Td className="font-mono text-xs">{formatDevEui(gateway.eui)}</Td>
                  <Td>{gateway.siteName ?? EM_DASH}</Td>
                  <Td>
                    <GatewayStatusBadge value={gateway.status} />
                    {gateway.connectedAt ? (
                      <p
                        className="mt-1 text-xs text-ink-muted"
                        title={
                          gateway.connectionCheckedAt
                            ? t("gatewayConnectionChecked", {
                                when: formatRelative(gateway.connectionCheckedAt),
                              })
                            : undefined
                        }
                      >
                        {t("gatewayConnectedSince", { when: formatRelative(gateway.connectedAt) })}
                      </p>
                    ) : null}
                  </Td>
                  <Td numeric>
                    {gateway.deviceCount > 0 ? (
                      // Two numbers rather than one: the gap between devices
                      // configured against this gateway and devices that have
                      // actually been heard through it is what tells you an
                      // install is half done.
                      <Link
                        to={`/devices?siteId=${gateway.siteId ?? ""}`}
                        className="text-brand underline underline-offset-2"
                        title={`${gateway.verifiedDeviceCount} of ${gateway.deviceCount} have reported through it`}
                      >
                        {gateway.verifiedDeviceCount}/{gateway.deviceCount}
                      </Link>
                    ) : (
                      EM_DASH
                    )}
                  </Td>
                  <Td>{formatRelative(gateway.lastSeenAt)}</Td>
                  {isSuperAdmin ? (
                    <Td>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={t("editGatewayAlert", { name: gateway.name })}
                        onClick={() => setEditingAlert(gateway)}
                      >
                        {t("gatewayAlertAfter", { minutes: gateway.offlineAlertAfterSeconds / 60 })}
                      </Button>
                    </Td>
                  ) : null}
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <Td colSpan={columnCount} className="border-b-0">
                  <Pagination
                    page={gateways.data.pagination.page}
                    totalPages={gateways.data.pagination.totalPages}
                    total={gateways.data.pagination.total}
                    noun={{
                      one: tCommon("noun.gateway.one"),
                      other: tCommon("noun.gateway.other"),
                    }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      <GatewayCreateDialog open={creating} onClose={() => setCreating(false)} />
      {editingAlert ? (
        <GatewayAlertDialog gateway={editingAlert} onClose={() => setEditingAlert(null)} />
      ) : null}
    </>
  );
}
